import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { useBasketMonitor, normalizeBasketSymbol } from "@trader-dashboard/shared";
import { formatCurrency } from "@/utils/formatCurrency";

type PairProfitCellProps = {
  symbol: string;
  /** Net floating P/L from the live feed (already includes swap + commission). */
  profit: number;
  totalProfit: number;
  openOrders: number;
};

const CostRow = ({ label, value, emphasized }: { label: string; value: string; emphasized?: boolean }): React.ReactNode => (
  <div className={`flex items-center justify-between gap-6 ${emphasized ? "font-semibold" : ""}`}>
    <span className="text-muted-foreground">{label}</span>
    <span className="tabular-nums">{value}</span>
  </div>
);

/**
 * The feed's per-pair profit is net of swap and commission, but carries no
 * breakdown — that comes from the synced open-order rows, which may cover
 * fewer orders than are actually open. Approximate figures are marked ≈.
 */
export const PairProfitCell = ({ symbol, profit, totalProfit, openOrders }: PairProfitCellProps): React.ReactNode => {
  const { pairCosts } = useBasketMonitor();
  const costs = pairCosts.get(normalizeBasketSymbol(symbol));

  const partialCoverage = costs !== undefined && (costs.syncedOrders < openOrders || costs.costsIncomplete);
  const approx = partialCoverage ? "≈" : "";

  const valueNode = (
    <div className="flex justify-end items-center space-x-2">
      {profit !== 0 && totalProfit !== 0 && (
        <span className="text-sm text-muted-foreground">
          ({((profit / totalProfit) * 100).toFixed(2)}%)
        </span>
      )}
      <div className={`font-medium text-lg cursor-help ${profit >= 0 ? "text-green-600" : "text-red-600"}`}>
        {formatCurrency(profit)}
      </div>
    </div>
  );

  return (
    <Tooltip>
      <TooltipTrigger asChild>{valueNode}</TooltipTrigger>
      <TooltipContent side="left" className="w-56">
        <div className="flex flex-col gap-1 text-xs">
          <p className="font-semibold">Net P/L — includes swap &amp; commission</p>
          {costs ? (
            <>
              <CostRow label="Price P/L" value={`${approx}${formatCurrency(profit - costs.swapUsd - costs.commissionUsd)}`} />
              <CostRow label="Swap" value={`${approx}${formatCurrency(costs.swapUsd)}`} />
              <CostRow label="Commission" value={`${approx}${formatCurrency(costs.commissionUsd)}`} />
              <CostRow label="Net" value={formatCurrency(profit)} emphasized />
              {costs.syncedOrders < openOrders && (
                <p className="pt-1 text-muted-foreground">
                  Costs cover {costs.syncedOrders} of {openOrders} open orders.
                </p>
              )}
              {costs.costsIncomplete && (
                <p className="pt-1 text-muted-foreground">
                  Some orders synced without cost fields — costs understated.
                </p>
              )}
            </>
          ) : (
            <p className="text-muted-foreground">
              {openOrders > 0
                ? "Breakdown unavailable — open orders not synced yet."
                : "No open orders."}
            </p>
          )}
        </div>
      </TooltipContent>
    </Tooltip>
  );
};
